// src/components/CatylexSearch/CatylexSearchSection.tsx
// =================================================================
// CATYLEX SEARCH SECTION - INLINE SEARCH RESULTS
// =================================================================
//
// A collapsible disclosure that appears inline within AI chat responses
// when the AI searches the Catylex document repository for contracts
// or clauses. Follows the WebToolsSection/ThinkingSection pattern.
// =================================================================

import * as React from "react";
import { useState, useCallback, useEffect, useId, useRef } from "react";
import {
  CATYLEX_READ_ERROR_DETAIL,
  CATYLEX_READ_ERROR_SUMMARY,
  CATYLEX_SEARCH_ERROR_DETAIL,
  CATYLEX_SEARCH_ERROR_SUMMARY,
  CATYLEX_SKIPPED_DETAIL,
  CATYLEX_SKIPPED_SUMMARY,
  NormalizedCatylexSearchResult,
} from "../../interfaces/catylexSearchTypes";
import { createLogger } from "../../utils/logger";
import CatylexLogo from "../CatylexLogo";
import CatylexContractList from "./CatylexContractList";
import CatylexSentenceList from "./CatylexSentenceList";
import CatylexNotSupported from "./CatylexNotSupported";
import CatylexDocumentCard from "./CatylexDocumentCard";
import { SearchIcon, DocumentIcon, ErrorIcon, ChevronIcon } from "./CatylexIcons";
import {
  catylexColors,
  containerStyle,
  createHeaderStyle,
  textContentStyle,
  headerTextStyle,
  separatorStyle,
  summaryTextStyle,
  errorBoxStyle,
  noteBoxStyle,
  createCollapseWrapperStyle,
  createExpandedContentStyle,
  infoBarStyle,
  infoLabelStyle,
  contentAreaStyle,
} from "./catylexSearchStyles";

const log = createLogger("CatylexSearchSection");

interface CatylexSearchSectionProps {
  result: NormalizedCatylexSearchResult;
}

const CatylexSearchSection: React.FC<CatylexSearchSectionProps> = ({ result }) => {
  const isRead = result.action === "read";
  // A second search in one response: the backend answered it without querying
  // Catylex. Neither a result nor a failure, and styled as neither — a warning
  // or error palette here would tell the user something went wrong when the
  // model simply already had its answer.
  const isSkipped = result.status === "skipped";

  // A failed read is the one case where the collapsed header isn't enough: the
  // remedy ("search within it instead") is copy the user can't guess, and it
  // only renders inside the panel. A failed search is self-explanatory — retype
  // the query — so it stays collapsed like every other result.
  //
  // Deliberately a lazy initializer, not an effect: `isExpanded` is otherwise
  // owned entirely by handleToggle, and an effect keyed on `result` would
  // re-open the panel on every parent re-render that hands us a freshly
  // normalized object, undoing the user's manual collapse.
  const [isExpanded, setIsExpanded] = useState(() => result.status === "error" && isRead);
  const headerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    log.debug("CatylexSearchSection mounted", {
      action: result.action,
      status: result.status,
      searchType: result.searchType,
      totalResults: result.totalResults,
    });
  }, [result]);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleToggle();
      }
    },
    [handleToggle]
  );

  const handleMouseEnter = useCallback(() => {
    if (headerRef.current) headerRef.current.style.backgroundColor = "#E9ECEF";
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (headerRef.current) headerRef.current.style.backgroundColor = "#F8F9FA";
  }, []);

  /** Compute actual result count, falling back to array length when total is missing */
  const getResultCount = (): number => {
    if (result.totalResults != null && result.totalResults > 0) return result.totalResults;
    if (result.contracts?.length) return result.contracts.length;
    if (result.sentences?.length) return result.sentences.length;
    return 0;
  };

  /** The label before the separator — "Catylex Search" is wrong for a loaded document. */
  const getHeaderLabel = (): string => (isRead ? "Catylex Document" : "Catylex Search");

  const getHeaderSummary = (): string => {
    if (result.status === "error") {
      return isRead ? CATYLEX_READ_ERROR_SUMMARY : CATYLEX_SEARCH_ERROR_SUMMARY;
    }
    if (result.status === "not_supported") {
      return result.reason || "Query not supported";
    }
    if (isSkipped) {
      // The backend's sentence, verbatim — it is the one that knows why.
      return result.skippedMessage || CATYLEX_SKIPPED_SUMMARY;
    }
    if (isRead) {
      return result.displayTitle || result.fileName || "Contract loaded";
    }
    if (result.resultsSummary) return result.resultsSummary;
    const typeLabel = result.searchType === "contracts" ? "contracts" : "clauses";
    const total = getResultCount();
    return `Found ${total} ${typeLabel} for "${result.originalQuery}"`;
  };

  const getIconStroke = (): string => {
    if (result.status === "error") return catylexColors.errorColor;
    if (result.status === "not_supported") return catylexColors.warningColor;
    if (isSkipped) return catylexColors.mutedForeground;
    return catylexColors.primary;
  };

  const getIconBg = (): string => {
    if (result.status === "error") return catylexColors.errorBg;
    if (result.status === "not_supported") return catylexColors.warningBg;
    if (isSkipped) return catylexColors.neutralBg;
    return catylexColors.primaryBg;
  };

  const getLabelColor = (): string => {
    if (result.status === "error") return catylexColors.errorColor;
    if (result.status === "not_supported") return catylexColors.warningColor;
    if (isSkipped) return catylexColors.mutedForeground;
    return catylexColors.foreground;
  };

  /** The info bar captions the query — and on a skip it was never sent. */
  const getQueryLabel = (): string => (isSkipped ? "Not searched:" : "Searched for:");

  const renderExpandedContent = () => {
    if (result.status === "error") {
      return (
        <div style={errorBoxStyle}>
          <ErrorIcon stroke={catylexColors.errorColor} />
          <div>
            {/* The body says what to do about it, so the fallback must never be
                the header summary — and never search-worded on a read. */}
            <p style={{ margin: 0, fontWeight: 500 }}>
              {result.errorMessage ||
                (isRead ? CATYLEX_READ_ERROR_DETAIL : CATYLEX_SEARCH_ERROR_DETAIL)}
            </p>
            {result.originalQuery && result.originalQuery !== "unknown" && (
              <p style={{ margin: "4px 0 0", opacity: 0.8 }}>
                Query: &ldquo;{result.originalQuery}&rdquo;
              </p>
            )}
          </div>
        </div>
      );
    }
    if (isSkipped) {
      // Ours, not the backend's `message`: the header already carried that. This
      // is the half the user can act on — the next search runs on the next reply.
      return (
        <div style={noteBoxStyle}>
          <p style={{ margin: 0 }}>{CATYLEX_SKIPPED_DETAIL}</p>
        </div>
      );
    }
    if (result.status === "not_supported") {
      return (
        <CatylexNotSupported
          reason={result.reason || ""}
          suggestions={result.suggestions || []}
          availableConcepts={result.availableConcepts || []}
        />
      );
    }
    if (isRead) {
      return <CatylexDocumentCard result={result} />;
    }
    if (result.searchType === "contracts" && result.contracts) {
      return <CatylexContractList contracts={result.contracts} total={getResultCount()} />;
    }
    if (result.sentences) {
      return <CatylexSentenceList sentences={result.sentences} total={getResultCount()} />;
    }
    return null;
  };

  const hasQuery = !!result.originalQuery && result.originalQuery !== "unknown";
  const showInfoBar = hasQuery || !!result.interpretedQuery;

  const iconContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    backgroundColor: getIconBg(),
    flexShrink: 0,
  };

  return (
    <div style={containerStyle}>
      <div
        ref={headerRef}
        style={createHeaderStyle(isExpanded)}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        // An aria-label overrides the visible label + summary below, so it has to
        // carry them: without the summary a screen-reader user hears neither which
        // contract this is nor, on the error path, that anything failed.
        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${getHeaderLabel()}: ${getHeaderSummary()}`}
      >
        <div style={iconContainerStyle}>
          {isRead ? (
            <DocumentIcon stroke={getIconStroke()} />
          ) : (
            <SearchIcon stroke={getIconStroke()} />
          )}
        </div>

        <div style={textContentStyle}>
          <p style={headerTextStyle}>
            <span style={{ color: getLabelColor(), fontWeight: 500 }}>{getHeaderLabel()}</span>
            <span style={separatorStyle}>·</span>
            <span style={summaryTextStyle}>{getHeaderSummary()}</span>
          </p>
        </div>

        <ChevronIcon isExpanded={isExpanded} />
      </div>

      <div
        id={panelId}
        style={createCollapseWrapperStyle(isExpanded)}
        aria-hidden={!isExpanded}
        // `inert` keeps focus and AT out of the collapsed panel; cast because
        // React's CSSProperties doesn't yet model the HTML attribute.
        {...({ inert: !isExpanded ? "" : undefined } as Record<string, string | undefined>)}
      >
        <div style={createExpandedContentStyle(isExpanded)}>
          {/* The bar exists to caption the query, so it renders only when there
              is one. A read has none: it takes a contract_id, and printing that
              id here made a 24-character internal handle the most prominent line
              in the panel — nothing the user can read, check, or act on. The
              card below names the contract and links to it in Catylex. */}
          {showInfoBar && (
            <div style={infoBarStyle}>
              <CatylexLogo height={20} variant="compact" />
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
                {hasQuery && (
                  <span>
                    <span style={infoLabelStyle}>{getQueryLabel()}</span> {result.originalQuery}
                  </span>
                )}
                {result.interpretedQuery && (
                  <span>
                    <span style={infoLabelStyle}>Interpreted as:</span>{" "}
                    <span style={{ fontStyle: "italic" }}>{result.interpretedQuery}</span>
                  </span>
                )}
              </div>
            </div>
          )}

          <div style={contentAreaStyle}>{renderExpandedContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default CatylexSearchSection;
